import { useEffect } from "react";
import { z } from "zod";
import { captureException, reset } from "./analytics";

export const apiUrl = import.meta.env.VITE_API_URL;

interface FetchApiOptions {
  body?: unknown;
  params?: Record<string, string>;
  /**
   * Forwarded to `fetch`. Set to `"include"` only for endpoints whose cookies
   * matter, since the app and API are separate origins in production.
   */
  credentials?: RequestCredentials;
}

const ErrorResponseSchema = z.object({ error: z.string() });

/**
 * Extracts an error message from a failed API response body.
 * @param data - The parsed response body
 * @param statusText - The HTTP status text to use as fallback
 * @returns The server's error message or the status text
 */
function extractError(data: unknown, statusText: string): string {
  const parsed = ErrorResponseSchema.safeParse(data);
  return parsed.success ? parsed.data.error : statusText || "Request failed";
}

/**
 * Makes an authenticated API request and returns the result.
 * @param path - The API path (appended to the base API URL)
 * @param method - The HTTP method
 * @param options - Optional request body, query parameters, and credentials mode
 * @returns An object with `ok`, `data`, and `error` (non-null when `ok` is false)
 */
export const fetchApi = async (
  path: string,
  method: "POST" | "GET" | "PUT" | "DELETE" | "PATCH",
  options?: FetchApiOptions,
): Promise<{
  ok: boolean;
  status: number;
  data: unknown;
  error: string | null;
}> => {
  let url = `${apiUrl}/${path}`;

  // Add query parameters if provided
  if (options?.params) {
    const queryParams = new URLSearchParams();
    Object.entries(options.params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        queryParams.append(key, value);
      }
    });

    const queryString = queryParams.toString();
    if (queryString) {
      url += `?${queryString}`;
    }
  }

  const headers: Record<string, string> = {};

  if (options?.body) {
    headers["Content-Type"] = "application/json";
  }

  // Add auth token if available
  const authToken = getAuthToken();
  if (authToken) {
    headers.Authorization = `Bearer ${authToken}`;
  }

  const fetchResponse = await fetch(url, {
    method,
    body: options?.body ? JSON.stringify(options.body) : undefined,
    headers,
    credentials: options?.credentials,
  });

  // Check if the response contains a refreshed token
  const newAuthToken = fetchResponse.headers.get("x-new-auth-token");
  if (newAuthToken) {
    saveAuthToken(newAuthToken);
  }

  // On 401, clear auth state so useRequireAuth triggers a redirect to login.
  // This prevents polling loops from hammering the backend after token expiry.
  if (fetchResponse.status === 401) {
    localStorage.removeItem(AUTH_TOKEN_KEY);
    reset();
    // Dynamic import to avoid circular dependency (authStore imports from api.ts)
    const { useAuthStore } = await import("@/store/authStore");
    useAuthStore.setState({ user: null, isLoading: false });
  }

  // Response codes that should not have a body
  const noContentStatuses = [204, 205, 304];

  if (noContentStatuses.includes(fetchResponse.status)) {
    return {
      ok: fetchResponse.ok,
      status: fetchResponse.status,
      data: undefined,
      error: fetchResponse.ok
        ? null
        : fetchResponse.statusText || "Request failed",
    };
  }

  // Check if there's actually a body to parse
  const contentLength = fetchResponse.headers.get("content-length");
  if (contentLength === "0" || !fetchResponse.body) {
    return {
      ok: fetchResponse.ok,
      status: fetchResponse.status,
      data: undefined,
      error: fetchResponse.ok
        ? null
        : fetchResponse.statusText || "Request failed",
    };
  }

  try {
    const data = await fetchResponse.json();
    return {
      ok: fetchResponse.ok,
      status: fetchResponse.status,
      data,
      error: fetchResponse.ok
        ? null
        : extractError(data, fetchResponse.statusText),
    };
  } catch (error) {
    captureException(error as Error);
    return {
      ok: fetchResponse.ok,
      status: fetchResponse.status,
      data: undefined,
      error: fetchResponse.ok
        ? null
        : fetchResponse.statusText || "Request failed",
    };
  }
};

export const AUTH_TOKEN_KEY = "AUTH_TOKEN";

export const saveAuthToken = (token: string) => {
  localStorage.setItem(AUTH_TOKEN_KEY, token);
};

export const getAuthToken = () => {
  return localStorage.getItem(AUTH_TOKEN_KEY);
};

export const getLoginUrl = () => {
  return `${apiUrl}/auth/login`;
};

export const checkLoggedIn = async () => {
  if (!getAuthToken()) {
    return false;
  }

  const response = await fetchApi(`users/me`, "GET");
  if (!response.ok) {
    localStorage.removeItem(AUTH_TOKEN_KEY);
    return false;
  }
  return true;
};

export const useIsLoggedIn = () => {
  useEffect(() => {
    const checkOrRedirect = async () => {
      const isLoggedIn = await checkLoggedIn();
      if (!isLoggedIn) {
        window.location.href = getLoginUrl();
      }
    };
    checkOrRedirect();
  }, []);
};
